import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { MenuButton, MenuStateProvider, MenuItem, Menu, MenuGroup, Icon, Box } from '@maui/elements'

export const Dropdown = ({
  children,
  trigger: propsTrigger,
  triggerLeft,
  triggerRight,
  ...props
}) => {
  const trigger = useMemo(props => {
    if (typeof propsTrigger === 'function') {
      return propsTrigger()
    }
    if (typeof propsTrigger === 'string' || typeof propsTrigger === 'number') {
      return (
        <DropdownTrigger slotLeft={triggerLeft} slotRight={triggerRight} inline>
          {propsTrigger}
        </DropdownTrigger>
      )
    }
    return propsTrigger
  }, [propsTrigger, triggerLeft, triggerRight])

  return (
    <MenuStateProvider>
      {trigger}
      <Menu width="164px" {...props}>
        {children}
      </Menu>
    </MenuStateProvider>
  )
}

Dropdown.defaultProps = {}

Dropdown.propTypes = {
  children: PropTypes.node,
  group: PropTypes.string.isRequired,
  trigger: PropTypes.node.isRequired,
  triggerLeft: PropTypes.node,
  triggerRight: PropTypes.node,
}

export const DropdownTrigger = ({ children, slotLeft, slotRight, ...props }) => {
  const renderSlot = prop => {
    if (!prop) return null
    if (typeof prop === 'string') return <Icon name={prop} />
    if (typeof prop === 'function') return prop()
    return prop
  }

  return (
    <MenuButton
      variant="secondary"
      userSelect="none"
      display="inline-flex"
      gap="spaceGapLarge"
      {...props}
    >
      {renderSlot(slotLeft)}
      {children}
      {renderSlot(slotRight)}
    </MenuButton>
  )
}

DropdownTrigger.propTypes = {
  children: PropTypes.node,
  slotLeft: PropTypes.node,
  slotRight: PropTypes.node,
}

export const DropdownItem = props => {
  const { children, icon, ...rest } = props

  return (
    <MenuItem
      alignItems="center"
      display="flex"
      flexDirection="row"
      gap="spaceGapLarge"
      color="colorActivePrimary"
      {...rest}
    >
      <Box fontWeight="700" flex="1">
        {children}
      </Box>
      {icon &&
        <Box flexShrink="0">
          {typeof icon === 'string' ? <Icon name={icon} /> : icon}
        </Box>
      }
    </MenuItem>
  )
}

DropdownItem.propTypes = {
  children: PropTypes.node,
  icon: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
}

export const DropdownLink = ({ external, ...props }) => {
  return (
    <DropdownItem
      target={external ? '_blank' : undefined}
      icon={props.icon || (external ? 'externalLink' : undefined)}
      {...props}
    />
  )
}

DropdownLink.defaultProps = {
  external: false,
}

DropdownLink.propTypes = {
  external: PropTypes.bool,
  icon: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
}

Dropdown.Link = DropdownLink
Dropdown.Item = DropdownItem
Dropdown.Trigger = DropdownTrigger
Dropdown.Group = MenuGroup
